import React, { useState, useCallback, useContext, useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import { useUpload } from "@/hooks";
import { FileSpec, IFileRef } from "@/client/types";
import { GenAiContext } from "@/client/context";
import { useRecoilValue } from "recoil";
import { accessTokenState, sessionIdState } from "@/client/state";
import UploadFileItem from "./UploadFileItem";

type Props = {
  fileSpec: FileSpec;
  companyId?: string;
  fileType: string;
  onFileUpload?: (files: File[]) => void;
  onFileUploadError?: (error: string) => void;
  onFileUpdated?: () => void;
  setFileUrl: (url: string) => void;
  setFilename: (url: string) => void;
  existingFilename?: string;
};

const InlineUploadFile: React.FC<Props> = ({
  fileSpec,
  companyId,
  fileType,
  onFileUpload,
  onFileUploadError,
  onFileUpdated,
  setFileUrl,
  setFilename,
  existingFilename,
}) => {
  const [uploads, setUploads] = useState<
    {
      progress: number;
      uploaded: boolean;
      cancel: () => void;
      fileRef: Partial<IFileRef>;
    }[]
  >(() =>
    existingFilename
      ? [
          {
            progress: 100,
            uploaded: true,
            cancel: () => {},
            fileRef: { name: existingFilename },
          },
        ]
      : []
  );

  useEffect(() => {
    if (existingFilename) {
      setUploads([
        {
          progress: 100,
          uploaded: true,
          cancel: () => {},
          fileRef: { name: existingFilename },
        },
      ]);
    }
  }, [existingFilename]);

  const uploading = uploads.some((upload) => !upload.uploaded);

  const accessToken = useRecoilValue(accessTokenState);
  const apiClient = useContext(GenAiContext);
  const sessionId = useRecoilValue(sessionIdState);

  const uploadAccountFile = useCallback(
    (file: File, onProgress: (progress: number) => void) => {
      if (companyId) {
        return apiClient.uploadAccountFile(
          file,
          onProgress,
          fileType,
          sessionId,
          accessToken,
          companyId
        );
      } else {
        return apiClient.uploadAccountFile(
          file,
          onProgress,
          fileType,
          sessionId,
          accessToken
        );
      }
    },
    [companyId, fileType, sessionId, accessToken, apiClient]
  );

  const upload = useUpload({
    spec: { ...fileSpec, max_files: 1 }, // force max_files = 1
    options: { disabled: uploading, multiple: false },
    onResolved: (files: File[]) => {
      if (!files.length) return;

      const file = files[0]; // only take first file

      const { xhr, promise } = uploadAccountFile(file, (progress) => {
        setUploads([
          {
            progress,
            uploaded: false,
            cancel: () => xhr.abort(),
            fileRef: { name: file.name, size: file.size },
          },
        ]);
      });

      promise
        .then((response) => {
          setUploads([
            {
              progress: 100,
              uploaded: true,
              cancel: () => xhr.abort(),
              fileRef: { name: file.name, size: file.size },
            },
          ]);
          setFileUrl(response?.data?.url);
          setFilename(file.name);
          onFileUpdated?.();
        })
        .catch((err) => {
          onFileUploadError?.(err);
          setUploads([]);
          setFileUrl("");
          setFilename("");
        });

      onFileUpload?.([file]);
    },
    onError: onFileUploadError,
  });

  if (!upload) return null;

  const { getRootProps, getInputProps } = upload;

  const handleRemove = useCallback(async () => {
    if (companyId && uploads[0]?.fileRef?.name) {
      try {
        const payload = {
          company_id: companyId,
          file_category: fileType,
          identifier: uploads[0].fileRef.name,
        };
        await apiClient.deleteAccountFile(payload, accessToken);
      } catch (err) {
        console.error("Failed to delete file via API:", err);
      }
    }
    setUploads([]);
    setFileUrl("");
    setFilename("");
  }, [
    uploads,
    companyId,
    fileType,
    accessToken,
    apiClient,
    setFileUrl,
    setFilename,
  ]);

  return (
    <>
      <Stack
        sx={{
          width: "100%",
          borderRadius: 1,
          border: (theme) => `1px dashed ${theme.palette.divider}`,

          cursor: "pointer",
          textAlign: "center",
        }}
        alignItems="center"
        justifyContent="center"
        gap={1}
        padding={3}
        {...getRootProps({ className: "dropzone" })}
      >
        <input
          {...getInputProps({ multiple: false })}
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            overflow: "hidden",
            opacity: 0,
          }}
        />

        <img src="/upload_file.svg" alt="upload file" />
        <Typography color="text.primary">Drag and drop file here</Typography>
        <Typography variant="caption" color="text.secondary">
          .pdf, .txt up to 100MB
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {/* If a new upload exists, show it with progress */}
        {uploads.map((upload, index) => (
          <UploadFileItem
            key={index}
            name={upload.fileRef?.name ?? "Untitled"}
            size={upload.fileRef?.size ?? undefined}
            progress={upload.progress}
            onRemove={handleRemove}
          />
        ))}
      </Stack>
    </>
  );
};

export default InlineUploadFile;
